import {
  KatDropdown,
  KatFilterBar,
  KatInput,
  KatPagination,
} from "@amzn/katal-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ValidationSeverity } from "src/api/interfaces/nest";
import type { NestTestResultError } from "src/api/interfaces/nest/history";
import DataTable from "src/components/DataTable";
import "./index.css";
import NestErrorDataTableRow from "./NestErrorDataTableRow";
import { buildNestErrorTableDefinition } from "./nest-error-data-table-definition";

type SortDirection = "asc" | "desc" | false;

const DEFAULT_PAGE_SIZE = 10;

interface NestHistoryErrorTableProps {
  data: NestTestResultError[];
}

export function NestHistoryErrorTable({ data }: NestHistoryErrorTableProps) {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState<SortDirection>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const filteredData = useMemo(() => {
    let result = data;

    if (severityFilter) {
      result = result.filter((item) => item.severity === severityFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(
        (item) =>
          t(
            item.message.key,
            item.message.parameters as Record<string, unknown>
          )
            .toLowerCase()
            .includes(term) ||
          t(item.error.key, item.error.parameters as Record<string, unknown>)
            .toLowerCase()
            .includes(term) ||
          item.element?.toLowerCase().includes(term) ||
          (item.line !== undefined && String(item.line).includes(term))
      );
    }

    return result;
  }, [data, searchTerm, severityFilter]);

  const sortedData = useMemo(() => {
    if (!sortDirection) return filteredData;
    return [...filteredData].sort((a, b) => {
      const cmp = a.severity.localeCompare(b.severity);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortDirection]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const toggleSort = () => {
    setSortDirection((prev) =>
      prev === "asc" ? "desc" : prev === "desc" ? false : "asc"
    );
  };

  const handleSearchChange = (e: KatInput.ChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    setSearchTerm(value);
    setPage(1);
  };

  const handleSeverityFilterChange = (e: KatDropdown.ChangeEvent) => {
    setSeverityFilter(e.detail.value);
    setPage(1);
  };

  const tableDefinition = useMemo(
    () =>
      buildNestErrorTableDefinition({
        sortDirection,
        onToggleSort: toggleSort,
      }),
    [sortDirection]
  );

  const severityOptions = [
    { name: t("aic_nest_history_error_filter_all"), value: "" },
    {
      name: t("aic_nest_history_error_severity_error"),
      value: ValidationSeverity.ERROR,
    },
    {
      name: t("aic_nest_history_error_severity_warning"),
      value: ValidationSeverity.WARNING,
    },
    {
      name: t("aic_nest_history_error_severity_info"),
      value: ValidationSeverity.INFO,
    },
  ];

  if (data.length === 0) {
    return (
      <div
        className="nest-history-error-table-empty"
        role="status"
        aria-live="polite"
      >
        <p>{t("aic_nest_history_error_table_empty")}</p>
      </div>
    );
  }

  return (
    <div
      className="nest-history-error-table-wrapper"
      role="region"
      aria-label={t("aic_nest_history_error_table_caption")}
    >
      <KatFilterBar>
        <KatInput
          type="text"
          slot="search"
          placeholder={t("aic_nest_history_error_search_placeholder")}
          value={searchTerm}
          onChange={handleSearchChange}
          aria-label={t("aic_nest_history_error_search_placeholder")}
        />
        <KatDropdown
          className="nest-history-error-table__severity-filter"
          options={severityOptions}
          value={severityFilter}
          onChange={handleSeverityFilterChange}
          placeholder={t("aic_nest_history_error_filter_severity")}
        />
      </KatFilterBar>

      {paginatedData.length === 0 ? (
        <div
          className="nest-history-error-table-no-results"
          role="status"
          aria-live="polite"
        >
          <p>{t("aic_nest_history_error_table_no_results")}</p>
        </div>
      ) : (
        <>
          <div className="nest-history-error-table-scroll">
            <DataTable
              id="nest-history-errors"
              tableDefinition={tableDefinition}
              contents={paginatedData}
              CustomDataTableRow={NestErrorDataTableRow}
              customDataTableRowProps={{ collapseKey: sortDirection }}
            />
          </div>
          <KatPagination
            page={page}
            totalItems={sortedData.length}
            itemsPerPage={pageSize}
            variant="footer"
            onPageChanged={(e) => setPage(e.detail.page)}
            onItemsPerPageChange={(e) => {
              setPageSize(e.detail.value);
              setPage(1);
            }}
            className="nest-history-error-table-pagination"
          />
        </>
      )}
    </div>
  );
}
